<script setup>
/**
 * One feature theme: heading, description, bullets and a screenshot — or,
 * when the theme names a `visual` instead of an `image`, a drawing of the
 * app from FeatureArt.vue.
 *
 * On desktop the showcase swaps a single instance of this between tabs. On
 * phones it renders one per theme, stacked, and the buttons above scroll
 * between them.
 */
import IconCheck from "../icons/IconCheck.vue";
import FeatureArt from "./FeatureArt.vue";

defineProps({
  theme: { type: Object, required: true },
});
</script>

<template>
  <div class="panel__inner">
    <div class="panel__copy">
      <h3 class="panel__title">{{ theme.title }}</h3>
      <p class="panel__desc">{{ theme.description }}</p>

      <ul class="panel__list" role="list">
        <li
          v-for="bullet in theme.bullets"
          :key="bullet.title"
          class="panel__item"
        >
          <span class="panel__tick"><IconCheck /></span>
          <div>
            <strong class="panel__item-title">{{ bullet.title }}</strong>
            <span class="panel__item-text">{{ bullet.text }}</span>
          </div>
        </li>
      </ul>
    </div>

    <figure v-if="!theme.image" class="panel__visual">
      <FeatureArt :visual="theme.visual" />
    </figure>

    <figure v-else class="panel__visual">
      <img
        :src="theme.image"
        :alt="theme.imageAlt"
        width="420"
        height="560"
        loading="lazy"
      />
    </figure>
  </div>
</template>

<style scoped>
.panel__inner {
  display: grid;
  gap: var(--space-7);
  align-items: center;
}

.panel__title {
  font-size: var(--text-2xl);
  margin-block-end: var(--space-3);
}

.panel__desc {
  color: var(--ink-muted);
  font-size: var(--text-lg);
  margin-block-end: var(--space-5);
  max-inline-size: 52ch;
}

.panel__list {
  display: grid;
  gap: var(--space-4);
  padding: 0;
  margin: 0;
  list-style: none;
}

.panel__item {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
}

.panel__tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 26px;
  block-size: 26px;
  flex-shrink: 0;
  margin-block-start: 2px;
  border-radius: 50%;
  background: var(--brand-soft);
  color: var(--brand-ink);
}

.panel__tick :deep(svg) {
  inline-size: 15px;
  block-size: 15px;
  stroke-width: 2.6;
}

.panel__item-title {
  display: block;
  color: var(--ink);
  font-weight: var(--weight-semibold);
}

.panel__item-text {
  color: var(--ink-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.panel__visual {
  margin: 0;
  display: flex;
  justify-content: center;
}

.panel__visual img {
  inline-size: min(100%, 360px);
  block-size: auto;
  border-radius: var(--radius-xl);
  border: var(--border);
  background: var(--surface);
  box-shadow: var(--lift-3);
}

@media (min-width: 900px) {
  .panel__inner {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  }

  .panel__visual {
    order: 2;
  }
}
</style>
